import { Link } from "react-router-dom";
import { ArrowRight, Globe2 } from "lucide-react";
import Reveal from "../components/Reveal";
import PageHeader from "../components/PageHeader";

const REGIONS = [
  { title: "North America", body: "USA, Canada — private label buyers, ethnic supermarket chains, foodservice distributors and industrial food manufacturers.", coords: "20°N — 100°W", accent: "from-brand-gold/25" },
  { title: "Europe", body: "UK, Germany, France, Netherlands, Italy, Poland — retail chains, wholesalers, condiment manufacturers and premium spice brands.", coords: "50°N — 10°E", accent: "from-brand-green/30" },
  { title: "Middle East", body: "UAE, Saudi Arabia, Qatar, Kuwait, Oman — HORECA suppliers, retail groups, food manufacturers and re-export hubs.", coords: "25°N — 55°E", accent: "from-brand-earth/40" },
  { title: "Asia-Pacific", body: "Japan, Singapore, Australia, Malaysia, South Korea — specialty importers, premium retailers and industrial buyers.", coords: "15°N — 120°E", accent: "from-brand-gold/25" },
  { title: "Africa", body: "South Africa, Kenya, Nigeria, Egypt — growing markets for both retail spice brands and food-processing raw materials.", coords: "5°S — 20°E", accent: "from-brand-green/30" },
];

export default function ExportMarkets() {
  return (
    <>
      <PageHeader
        eyebrow="Global Reach"
        title="Serving Global Markets with Premium Indian Spice Solutions."
        italicWord="Global"
        subtitle="Our export operations are structured to handle the documentation, logistics and quality expectations of buyers across five continents."
      />

      {/* WORLD BAND */}
      <section className="py-24 bg-brand-ink text-brand-ivory relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img src="/images/hero/hero-export.png" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="container-luxe relative">
          <Reveal className="max-w-2xl">
            <span className="overline text-brand-gold-soft">Where we ship</span>
            <h2 className="font-serif text-4xl md:text-5xl mt-4 leading-tight">Five continents. One export standard.</h2>
            <span className="divider-gold" />
            <p className="text-brand-ivory/65 leading-relaxed">India is uniquely positioned as the world's largest spice producer, consumer and exporter. From Nhava Sheva and Mundra ports on the west, and Chennai on the east, our shipments reach global markets with the documentation and consistency serious buyers require.</p>
          </Reveal>

          {/* Stylized world dots grid */}
          <Reveal delay={200} className="mt-16">
            <div className="relative aspect-[16/8] border border-brand-ivory/10 rounded overflow-hidden bg-brand-ink">
              <svg viewBox="0 0 1600 800" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid meet">
                {/* Continents as dot cluster (procedural, subtle) */}
                {Array.from({ length: 900 }).map((_, i) => {
                  const x = Math.random() * 1600;
                  const y = Math.random() * 800;
                  // Loose land mass simulation
                  const inLand = (
                    (x > 200 && x < 480 && y > 120 && y < 500) || // N. America
                    (x > 480 && x < 640 && y > 400 && y < 720) || // S. America
                    (x > 720 && x < 950 && y > 100 && y < 320) || // Europe
                    (x > 800 && x < 1080 && y > 320 && y < 620) || // Africa
                    (x > 950 && x < 1220 && y > 140 && y < 400) || // Asia
                    (x > 1200 && x < 1420 && y > 480 && y < 640)   // Australia
                  );
                  if (!inLand) return null;
                  return <circle key={i} cx={x} cy={y} r={1.2} fill="#D4A017" opacity="0.35" />;
                })}
                {/* Origin: India */}
                <circle cx={1100} cy={320} r={9} fill="#D4A017" />
                <circle cx={1100} cy={320} r={20} fill="#D4A017" opacity="0.2">
                  <animate attributeName="r" values="9;40;9" dur="3s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.5;0;0.5" dur="3s" repeatCount="indefinite" />
                </circle>
                {/* Trade routes */}
                {[
                  [1100, 320, 380, 260], // to N. America
                  [1100, 320, 830, 220], // to Europe
                  [1100, 320, 1000, 400], // Middle East
                  [1100, 320, 1300, 280], // Asia-Pacific
                  [1100, 320, 950, 500], // Africa
                ].map(([x1, y1, x2, y2], i) => (
                  <path
                    key={i}
                    d={`M${x1},${y1} Q${(x1 + x2) / 2},${Math.min(y1, y2) - 100} ${x2},${y2}`}
                    stroke="#D4A017"
                    strokeWidth="1.2"
                    fill="none"
                    strokeDasharray="4 4"
                    opacity="0.55"
                  />
                ))}
                {/* Destination pins */}
                {[[380, 260, "North America"], [830, 220, "Europe"], [1000, 400, "Middle East"], [1300, 280, "Asia-Pacific"], [950, 500, "Africa"]].map(([x, y, label]) => (
                  <g key={label}>
                    <circle cx={x} cy={y} r={5} fill="#FFFDF7" />
                    <circle cx={x} cy={y} r={5} fill="none" stroke="#D4A017" strokeWidth="1" />
                  </g>
                ))}
              </svg>
              <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-[10px] tracking-[0.3em] uppercase text-brand-ivory/50">
                <span>Origin · India</span>
                <span>Destinations · 5 continents</span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* REGIONS */}
      <section className="py-24 md:py-32 bg-brand-ivory">
        <div className="container-luxe">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {REGIONS.map((r, i) => (
              <Reveal key={r.title} delay={i * 80}>
                <div className={`relative overflow-hidden p-8 h-full border border-brand-charcoal/10 bg-gradient-to-br ${r.accent} to-transparent hover:border-brand-gold transition-all duration-500`}>
                  <Globe2 className="w-6 h-6 text-brand-green mb-6" />
                  <div className="text-[10px] tracking-[0.3em] uppercase text-brand-charcoal/40 mb-2">{r.coords}</div>
                  <h3 className="font-serif text-3xl text-brand-charcoal mb-4">{r.title}</h3>
                  <p className="text-brand-charcoal/70 text-[14.5px] leading-relaxed">{r.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* NOTE + CTA */}
      <section className="py-24 bg-brand-ivory-warm">
        <div className="container-luxe max-w-3xl text-center">
          <p className="text-brand-charcoal/60 italic text-[15px] leading-relaxed">Country lists indicate our target export corridors. Specific country references for existing orders are available upon buyer verification and NDA.</p>
          <div className="mt-10">
            <Link to="/request-quote" className="luxe-btn-primary" data-testid="markets-cta">Ship to your market <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>
    </>
  );
}
